import { Draggable, Droppable } from "@hello-pangea/dnd";
import { apiMachineType, DragDndItemsType } from "@/common/type/type";
import DragBox from "@/organisms/dragBox";
import { Box, Grid, Stack } from "@/common/styles/style";
import { styles } from "./style";
import {
  DataItemType,
  DataViewType,
  GetDataType,
  GetTrendType,
  TrendItemType,
  TrendViewType,
  WidgetDataType,
  WidgetType,
} from "@/pages/dashboard/type";
import { useEffect, useState } from "react";
import { formatToApiDateTime, lastWeek, today } from "@/helpers/getUtcOffset";
import { useLoading } from "@/hooks/useLoading";
import CircularProgress from "@/molecules/circularProgress";

interface DragAreaProps {
  dragAreaItems: DragDndItemsType;
  selectedMachine: apiMachineType;
  widgetQueue?: WidgetType[];
  dataWidgets: DataViewType[];
  trendWidgets: TrendViewType[];
  setDataWidgets: React.Dispatch<React.SetStateAction<DataViewType[]>>;
  setTrendWidgets: React.Dispatch<React.SetStateAction<TrendViewType[]>>;
  setTotalWidgets: React.Dispatch<React.SetStateAction<WidgetDataType[]>>;
  onUpdateWidgets?: (widgets: WidgetDataType[]) => void;
  modify: boolean;
}

export default function DragArea({
  dragAreaItems,
  selectedMachine,
  widgetQueue,
  dataWidgets,
  trendWidgets,
  setDataWidgets,
  setTrendWidgets,
  setTotalWidgets,
  onUpdateWidgets,
  modify,
}: DragAreaProps) {
  const [widgetDataMap, setWidgetDataMap] = useState<
    Record<number, WidgetDataType>
  >({});
  const startDate = formatToApiDateTime(lastWeek);
  const endDate = formatToApiDateTime(today);
  const { loading, startLoading, stopLoading } = useLoading();
  const droppableId = `dragArea${selectedMachine.id}`;

  useEffect(() => {
    if (!widgetQueue || widgetQueue.length === 0) return;

    // ウィジェット表示情報一覧取得API
    const fetchWidgetInfo = async () => {
      const newWidgetMap = { ...widgetDataMap };

      for (const { machineId, widgetId, widgetType } of widgetQueue) {
        try {
          startLoading();
          const endpoint = widgetType === 1 ? "data" : "trend";
          const queryStringParam = `${
            widgetType === 1 ? "dataWidgetIds" : "trendWidgetIds"
          }=${widgetId}`;

          const response = await fetch(
            `/api/dashboard/widget/${endpoint}?${queryStringParam}`,
            {
              method: "GET",
              credentials: "include",
            }
          );

          const data = await response.json();
          if (response.ok) {
            console.log(data.message);
            const entries = Object.entries(data.result[0]);

            for (const [_, value] of entries) {
              const typedValue = value as GetDataType | GetTrendType;

              await getSavedCollectedData({
                machineId,
                widgetType,
                value: typedValue,
                widgetId,
              });

              if (!newWidgetMap[machineId]) {
                newWidgetMap[machineId] = {
                  machineId,
                  dataWidgets: [],
                  trendWidgets: [],
                };
              }

              if (widgetType === 1) {
                if (
                  !newWidgetMap[machineId].dataWidgets.some(
                    (widget) =>
                      JSON.stringify(widget) === JSON.stringify(typedValue)
                  )
                ) {
                  newWidgetMap[machineId].dataWidgets.push(
                    typedValue as GetDataType
                  );
                }
              } else {
                if (
                  !newWidgetMap[machineId].trendWidgets.some(
                    (widget) =>
                      JSON.stringify(widget) === JSON.stringify(typedValue)
                  )
                ) {
                  newWidgetMap[machineId].trendWidgets.push(
                    typedValue as GetTrendType
                  );
                }
              }
            }
          } else {
            console.error("サーバ接続エラー: ", response.statusText);
          }
        } catch (error) {
          console.log("NO COLLECTED DATA");
        } finally {
          stopLoading();
        }
      }
      setWidgetDataMap(newWidgetMap);
      const updatedWidgets = Object.values(newWidgetMap);
      onUpdateWidgets?.(updatedWidgets);
    };

    fetchWidgetInfo();
  }, []);

  // ウィジェット収集データ取得API
  const getSavedCollectedData = async ({
    machineId,
    widgetType,
    value,
    widgetId,
  }: WidgetType) => {
    try {
      let dataNames: string[] = (value as GetDataType).dataNames
        ? [...(value as GetDataType).dataNames]
        : [];

      for (let i = 1; i <= 5; i++) {
        const key = `dataName${i}` as keyof GetTrendType;
        const data = (value as GetTrendType)[key];

        if (typeof data === "string") {
          dataNames.push(data);
        }
        if (dataNames[0] === "") {
          dataNames.shift();
        }
      }

      if (dataNames.length === 0) {
        dataNames = [""];
      }

      if (dataNames.length === 1 && dataNames[0] === "") {
        if (widgetType === 1) {
          setDataWidgets((prevData: DataViewType[]) => {
            if (prevData.some((widget) => widget.widgetId === widgetId)) {
              return prevData;
            }

            return [
              ...prevData,
              {
                machineId,
                widgetId,
                data: [],
                position: (value as GetDataType).position,
                sortId: 0,
              },
            ];
          });
        } else {
          setTrendWidgets((prevData: TrendViewType[]) => {
            if (prevData.some((widget) => widget.widgetId === widgetId)) {
              return prevData;
            }

            return [
              ...prevData,
              {
                machineId,
                widgetId,
                data: [],
                position: (value as GetTrendType).position,
                sortId: 0,
              },
            ];
          });
        }
        return;
      }

      for (const dataName of dataNames) {
        const queryParams = `machineId=${machineId}&dataName=${dataName}`;
        let url = `/api/dashboard/widget/${
          widgetType === 1 ? "data" : "trend"
        }/collectedData?${queryParams}`;

        if (widgetType === 2) {
          url += `&startAt=${startDate}&endAt=${endDate}`;
        }

        const response = await fetch(url, {
          method: "GET",
          credentials: "include",
        });

        const data = await response.json();
        if (response.ok) {
          const result = data.result.map(
            (item: DataViewType | TrendViewType) => {
              const { machineId: itemMachineId, ...rest } = item;
              return { machineId: itemMachineId, data: rest };
            }
          );

          if (widgetType === 1) {
            setDataWidgets((prevData: DataViewType[]) => {
              const existingWidget = prevData.find(
                (widget) =>
                  widget.machineId === machineId && widget.widgetId === widgetId
              );

              let updatedState: DataViewType[];

              if (existingWidget) {
                updatedState = prevData.map((widget) =>
                  widget.machineId === machineId && widget.widgetId === widgetId
                    ? {
                        ...widget,
                        data: [
                          ...(widget.data ?? []),
                          ...result.map((item: DataItemType) => item.data),
                        ],
                        position: (value as GetDataType).position,
                        sortId: widget.sortId,
                      }
                    : widget
                );
              } else {
                updatedState = [
                  ...prevData,
                  {
                    machineId,
                    widgetId,
                    data:
                      result.length > 0
                        ? result.map((item: DataItemType) => item.data)
                        : [],
                    position: (value as GetDataType).position,
                    sortId: dragAreaItems.find(
                      (item) =>
                        item.position === (value as GetDataType).position
                    )?.sortId,
                  },
                ];
              }

              return updatedState.map((widget) => ({
                ...widget,
                data: Array.from(
                  new Map(
                    widget.data?.map((item: DataItemType) => [
                      JSON.stringify(item),
                      item,
                    ])
                  ).values()
                ),
              }));
            });
          } else {
            setTrendWidgets((prevData: TrendViewType[]) => {
              const existingWidget = prevData.find(
                (widget) =>
                  widget.machineId === machineId && widget.widgetId === widgetId
              );

              let updatedState: TrendViewType[];

              if (existingWidget) {
                updatedState = prevData.map((widget) =>
                  widget.machineId === machineId && widget.widgetId === widgetId
                    ? {
                        ...widget,
                        data: [
                          ...(widget.data ?? []),
                          ...result.map((item: TrendItemType) => item.data),
                        ],
                        position: (value as GetTrendType).position,
                        sortId: widget.sortId,
                      }
                    : widget
                );
              } else {
                updatedState = [
                  ...prevData,
                  {
                    machineId,
                    widgetId,
                    data:
                      result.length > 0
                        ? result.map((item: TrendItemType) => item.data)
                        : [],
                    position: (value as GetTrendType).position,
                    sortId: dragAreaItems.find(
                      (item) =>
                        item.position === (value as GetTrendType).position
                    )?.sortId,
                  },
                ];
              }

              return updatedState.map((widget) => ({
                ...widget,
                data: Array.from(
                  new Map(
                    widget.data?.map((item: TrendItemType) => [
                      JSON.stringify(item),
                      item,
                    ])
                  ).values()
                ),
              }));
            });
          }
        } else {
          console.error("サーバ接続エラー:", response.statusText);
        }
      }
    } catch (error) {
      console.error("サーバ接続エラー:", error);
    }
  };

  return (
    <Stack sx={styles.container}>
      {loading ? (
        <CircularProgress />
      ) : (
        <Droppable droppableId={droppableId} direction="horizontal">
          {(provided) => (
            <Grid
              container
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={styles.gridContainer}
            >
              {dragAreaItems.map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={item.id.toString()}
                    index={index}
                  >
                    {(provided) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={styles.gridbox}
                      >
                        <Grid item xs="auto">
                          <DragBox
                            boxId={item.id}
                            sortId={item.sortId}
                            type={item.type}
                            selectedMachine={selectedMachine}
                            data={
                              item.type === 1
                                ? (dataWidgets.find(
                                    (data) => data.position === item.position
                                  ) as DataViewType)
                                : (trendWidgets.find(
                                    (data) => data.position === item.position
                                  ) as TrendViewType)
                            }
                            setTotalWidgets={setTotalWidgets}
                            dragAreaItems={dragAreaItems}
                            modify={modify}
                            onUpdateWidgets={onUpdateWidgets}
                          />
                        </Grid>
                      </Box>
                    )}
                  </Draggable>
              ))}
              {provided.placeholder}
            </Grid>
          )}
        </Droppable>
      )}
    </Stack>
  );
}
